import { observer } from 'mobx-react';
import React from 'react';
import { CATEGORY_MODAL_CATEGORY, CATEGORY_MODAL_PRODUCT, CATEGORY_MODAL_SECTIONS, ICategoryOrProductItem } from '../../CategoryAndProductModal';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import { PRODUCT_TYPE } from 'src/types/product';
import { getTranslate, getUnavailable } from 'src/utils/helpers';
import Icon from 'src/view/components/Controls/Icon/Icon';

interface IProps {
  selectedCategories: ICategoryOrProductItem[];
  selectedProducts: ICategoryOrProductItem[];
  isCategories: CATEGORY_MODAL_CATEGORY;
  isProducts: CATEGORY_MODAL_PRODUCT;
  save: (categories: ICategoryOrProductItem[], products: ICategoryOrProductItem[]) => void;
  //
  productTypes?: PRODUCT_TYPE[];
  categoryInputValid: boolean;
  productInputValid: boolean;
  productInput?: boolean;
  className?: string;
  titleClassName?: string;
  wrapperClassName?: string;
  iconSize?: number;
  /**
   * Show Names Category and Products when they are SINGLE
   *
   * @type {boolean}
   * @memberof IProps
   */
  showNames?: boolean;
}

interface IState {
  placeholderCategory: string;
  placeholderProduct: string;
  isAllCategories: boolean;
  isAllProducts: boolean;
}

@observer
export default class CategoryAndProductModalOpen extends React.Component<IProps, IState> {
  static defaultProps = {
    selectedCategories: [],
    selectedProducts: [],
    isCategories: CATEGORY_MODAL_CATEGORY.MULTIPLE,
    isProducts: CATEGORY_MODAL_PRODUCT.MULTIPLE,
    categoryInputValid: true,
    productInputValid: true,
  };
  // already selected unavailable
  selectedUnavailableCategories: ICategoryOrProductItem[] = [];
  // already selected unavailable
  selectedUnavailableProducts: ICategoryOrProductItem[] = [];
  state = {
    placeholderCategory:
      this.props.isCategories === CATEGORY_MODAL_CATEGORY.SINGLE ? localeStore.t('select_category') : localeStore.t('select_categories'),
    placeholderProduct: this.props.isProducts === CATEGORY_MODAL_PRODUCT.SINGLE ? localeStore.t('select_item') : localeStore.t('select_items'),
    isProducts: this.props.isProducts,
    isAllCategories: this.props.isCategories === CATEGORY_MODAL_CATEGORY.MULTIPLE && this.props.selectedCategories.length === 0,
    isAllProducts: this.props.isProducts === CATEGORY_MODAL_PRODUCT.MULTIPLE && this.props.selectedProducts.length === 0,
  };

  componentDidMount = () => {
    this.getArraysUnavailable();
    if (this.props.isProducts === CATEGORY_MODAL_PRODUCT.NONE) {
      this.changeCategoryPlaceholder(this.props.selectedCategories);
    } else {
      this.changePlaceholders(this.props.selectedCategories, this.props.selectedProducts);
    }
  };

  componentDidUpdate = (prevProps: IProps) => {
    if (this.props.selectedCategories !== prevProps.selectedCategories || this.props.selectedProducts !== prevProps.selectedProducts) {
      this.getArraysUnavailable();
    }
  };

  getArraysUnavailable = () => {
    this.selectedUnavailableCategories = this.props.selectedCategories.filter((category) => getUnavailable(category));
    this.selectedUnavailableProducts = this.props.selectedProducts.reduce(
      (sum: ICategoryOrProductItem[], current: ICategoryOrProductItem) =>
        sum.concat(
          getUnavailable(current) || this.selectedUnavailableCategories.some((category) => category.id === current.category_id) ? current : []
        ),
      []
    );
  };

  changePlaceholders = (categories: ICategoryOrProductItem[], products: ICategoryOrProductItem[]) => {
    this.changeCategoryPlaceholder(categories);
    this.changeProductPlaceholder(products);
  };

  changeCategoryPlaceholder = (category: ICategoryOrProductItem[] = []) => {
    const length: number = category.length - this.selectedUnavailableCategories.length;
    if (this.state.isAllCategories && this.props.isCategories === CATEGORY_MODAL_CATEGORY.MULTIPLE) {
      this.setState({ placeholderCategory: `${localeStore.t('all')} ${localeStore.t('selected')}` });
    } else if (length && length > 0) {
      if (length === 1) {
        this.setState({ placeholderCategory: getTranslate(category[0].name) });
      } else {
        this.setState({ placeholderCategory: `${length} ${localeStore.t('selected')}` });
      }
    } else {
      let placeholderCategory =
        this.props.isCategories === CATEGORY_MODAL_CATEGORY.SINGLE ? localeStore.t('select_category') : localeStore.t('select_categories');
      // Show Category name
      if (this.props.showNames && category.length) {
        placeholderCategory = getTranslate(category[0].name);
      }
      this.setState({
        placeholderCategory,
      });
    }
  };

  changeProductPlaceholder = (product: ICategoryOrProductItem[] = []) => {
    const length: number = product.length - this.selectedUnavailableProducts.length;
    if (this.state.isAllProducts && this.props.isProducts === CATEGORY_MODAL_PRODUCT.MULTIPLE) {
      this.setState({ placeholderProduct: `${localeStore.t('all')} ${localeStore.t('selected')}` });
    } else if (length && length > 0 && this.props.isProducts !== CATEGORY_MODAL_PRODUCT.NONE) {
      if (length === 1) {
        this.setState({ placeholderProduct: getTranslate(product[0].name) });
      } else {
        this.setState({ placeholderProduct: `${length} ${localeStore.t('selected')}` });
      }
    } else {
      let placeholderProduct = this.props.isProducts === CATEGORY_MODAL_PRODUCT.SINGLE ? localeStore.t('select_item') : localeStore.t('select_items');
      // Show Product name
      if (this.props.showNames && product.length) {
        placeholderProduct = getTranslate(product[0].name);
      }
      this.setState({
        placeholderProduct,
      });
    }
  };

  onSave = (categories: ICategoryOrProductItem[] = [], products: ICategoryOrProductItem[] = []) => {
    this.setState(
      {
        isAllCategories: categories.length === 0 && this.props.isCategories === CATEGORY_MODAL_CATEGORY.MULTIPLE,
        isAllProducts: products.length === 0 && this.props.isProducts === CATEGORY_MODAL_PRODUCT.MULTIPLE,
      },
      () => this.changePlaceholders(categories, products)
    );
    this.props.save(categories, products);
    modalStore.closeCategoryAndProductModal();
  };

  onSubmit = (section?: CATEGORY_MODAL_SECTIONS) => {
    modalStore.openCategoryAndProductModal({
      selectedCategories: this.props.selectedCategories,
      selectedProducts: this.props.selectedProducts,
      isCategories: this.props.isCategories,
      isProducts: this.props.isProducts,
      productTypes: this.props.productTypes,
      selectedUnavailableCategories: this.selectedUnavailableCategories,
      selectedUnavailableProducts: this.selectedUnavailableProducts,
      isAllCategories: this.state.isAllCategories,
      isAllProducts: this.state.isAllProducts,
      defaultSection: section,
      onSave: this.onSave,
    });
  };

  getCategoryInputClassName = () => {
    let className: string = '';
    if (this.props.className) className += ` ${this.props.className}`;
    if (!this.props.categoryInputValid) className += ' input-error';
    return className;
  };

  getProductInputClassName = () => {
    let className: string = '';
    if (this.props.className) className += ` ${this.props.className}`;
    if (!this.props.productInputValid) className += ' input-error';
    return className;
  };

  render() {
    return (
      <div className={`d-flex flex-row ${this.props.wrapperClassName || ''}`}>
        <div className={`custom-modal__open ${this.getCategoryInputClassName()}`} onClick={() => this.onSubmit()}>
          <p className={`family-medium text-size-16 color-dark ${this.props.titleClassName || ''}`}>{this.state.placeholderCategory}</p>
          <Icon name={Icons.Chevron} size={this.props.iconSize || 20} className="color-gray2" />
        </div>
        {this.props.productInput ? (
          <div className={`custom-modal__open ml-4 ${this.getProductInputClassName()}`} onClick={() => this.onSubmit(CATEGORY_MODAL_SECTIONS.ITEMS)}>
            <p className={`family-medium text-size-16 color-dark ${this.props.titleClassName || ''}`}>{this.state.placeholderProduct}</p>
            <Icon name={Icons.Chevron} size={this.props.iconSize || 20} className="color-gray2" />
          </div>
        ) : null}
      </div>
    );
  }
}
